import * as React from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { makeStyles } from "@material-ui/styles";
import TableSortLabel from "@mui/material/TableSortLabel";
import { stableSort, getComparator, Order } from "./sort";
import { colors, fontSizes } from "../../theme";

interface EnhancedTableProps {
    onRequestSort: (event: React.MouseEvent<unknown>, property: any) => void;
    order: Order;
    orderBy: any;
    headers: any[];
}

function EnhancedTableHead(props: EnhancedTableProps) {
    const { order, orderBy, onRequestSort, headers } = props;
    const createSortHandler = (property: any) => (
        event: React.MouseEvent<unknown>
    ) => {
        onRequestSort(event, property);
    };

    const theme = useStyles();

    return (
        <TableHead>
            <TableRow>
                {headers.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={
                            headCell.headerAlignRight || headCell.numeric
                                ? "right"
                                : "left"
                        }
                        padding={headCell.disablePadding ? "none" : "normal"}
                        sortDirection={orderBy === headCell.id ? order : false}
                        className={theme.emphasized}
                        sx={{
                            pt: 2,
                            pb: 2,
                        }}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id)}
                            sx={{
                                width: "max-content",
                            }}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span">
                                    {order === "desc" ? "" : ""}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: colors.white.main,
    },
    highlight: {
        backgroundColor: colors.green.select,
    },
    emphasized: {
        fontWeight: "bold",
    },
}));

interface ExpandControlProps {
    isExpanded: boolean;
    rowId: string;
    onExpandMethod?: (any) => any;
    onUnexpandMethod?: (any) => any;
    rowData: any;
}

const RowExpandControls: React.FC<ExpandControlProps> = (
    props: ExpandControlProps
) => {
    return (
        <TableCell>
            <IconButton aria-label="expand row" size="small">
                {props.isExpanded ? (
                    <KeyboardArrowUpIcon />
                ) : (
                    <KeyboardArrowDownIcon />
                )}
            </IconButton>
        </TableCell>
    );
};

export interface HeadCell {
    disablePadding?: boolean;
    id: string;
    label: string;
    numeric: boolean;
    displayField?: string;
    grow?: number;
    wrap?: boolean;
    align?: string;
    format?: any;
    headerAlignRight?: boolean;
}
interface RowProps {
    isExpanded: boolean;
    isExpandable: boolean;
    row: any;
    rowIdField: string;
    headers: any[];
    expandableComponent: React.FC<any>;
    expandableProps: any;
    onExpandMethod?: any;
    onUnexpandMethod?: any;
    onRowClickMethod?: any;
    changeColorOnSelect?: boolean;
}

function Row(props: RowProps) {
    const { row, isExpanded } = props;
    const [selected, setSelected] = React.useState(false);
    const theme = useStyles();

    let className = isExpanded ? theme.highlight : theme.root;

    if (props.changeColorOnSelect) {
        className = selected ? theme.highlight : theme.root;
    }
    const rowId = row[props.rowIdField];

    const ExpandableComponent = props.expandableComponent;
    const ExpandableControls = props.isExpandable ? (
        <RowExpandControls
            isExpanded={isExpanded}
            rowId={rowId}
            onUnexpandMethod={props.onUnexpandMethod}
            onExpandMethod={props.onExpandMethod}
            rowData={row}
        />
    ) : (
        <Box />
    );

    return (
        <React.Fragment>
            <TableRow
                className={className}
                sx={{ "& > *": { borderBottom: "unset" } }}
                hover
                onClick={(event) => {
                    setSelected(!selected);
                    if (props.onRowClickMethod) {
                        props.onRowClickMethod();
                    }
                    if (props.isExpanded) {
                        if (props.onUnexpandMethod) {
                            props.onUnexpandMethod(row);
                        }
                    } else {
                        if (props.onExpandMethod) {
                            props.onExpandMethod(row);
                        }
                    }
                }}
            >
                {props.headers.map((x: HeadCell, index: number) => {
                    let val = row[x.id];
                    if (x.displayField) {
                        val = row[x.displayField];
                    }
                    if (!Object.keys(row).includes(x.id)) {
                        return;
                    }
                    if (!val) {
                        val = " ";
                    }

                    val = x.format(val);
                    const alignCategory = x.numeric ? "right" : "left";
                    return (
                        <TableCell
                            key={`valtype_${index}`}
                            align={alignCategory}
                        >
                            {val}
                        </TableCell>
                    );
                })}
                {ExpandableControls}
            </TableRow>
            <TableRow
                sx={{
                    "& > *": { borderBottom: "unset" },
                }}
            >
                <TableCell
                    style={{
                        paddingBottom: 0,
                        paddingTop: 0,
                        paddingRight: 0,
                    }}
                    colSpan={props.headers.length}
                >
                    <Collapse
                        in={props.isExpanded}
                        timeout="auto"
                        unmountOnExit
                    >
                        {(() => {
                            if (props.isExpandable) {
                                return (
                                    <ExpandableComponent
                                        id={rowId}
                                        {...props}
                                    />
                                );
                            } else {
                                return <Box />;
                            }
                        })()}
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

interface ExpandableTableProps {
    tableHeaders: any[];
    tableData: any[];
    tableProps: any;
    initialSortColumn: string;
    initialSortDirection?: Order;
    expandableComponent: React.FC<any>;
    rowIdField: string;
    expandableProps: any;
    onExpandMethod?: any;
    onUnexpandMethod?: any;
    onRowClickMethod?: any;
    initialRowCount?: number;
    changeRowColorOnSelect?: boolean;
    pagination?: boolean;
}

export const ExpandableTable: React.FC<ExpandableTableProps> = (
    props: ExpandableTableProps
) => {
    const changeRowColorOnSelect = false || props.changeRowColorOnSelect;
    const initialRowCount = props.initialRowCount || 10;
    const initialOrder = props.initialSortDirection
        ? props.initialSortDirection
        : "asc";
    const [order, setOrder] = React.useState<Order>(initialOrder);
    const [orderBy, setOrderBy] = React.useState<any>(props.initialSortColumn);
    const [selected, setSelected] = React.useState<readonly string[]>([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(initialRowCount);

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: any
    ) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const emptyRows =
        page > 0
            ? Math.max(0, (1 + page) * rowsPerPage - props.tableData.length)
            : 0;

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSelectAllClick = () => {};

    const paginationComponent = props.pagination ? (
        <TablePagination
            rowsPerPageOptions={[
                initialRowCount,
                50,
                { label: "全て", value: props.tableData.length },
            ]}
            component="div"
            colSpan={props.tableHeaders.length}
            count={props.tableData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            labelRowsPerPage="表示件数"
            SelectProps={{
                inputProps: {
                    "aria-label": "件数",
                },
                native: true,
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
        />
    ) : (
        <Box />
    );

    return (
        <Box sx={{ width: "100%" }}>
            <TableContainer
                sx={{ width: "100%", cursor: "pointer" }}
                component={Paper}
            >
                <Table
                    id="Expandable-Table-inner"
                    aria-label="collapsible table"
                >
                    <EnhancedTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        headers={props.tableHeaders}
                    />
                    <TableBody>
                        {stableSort(
                            props.tableData,
                            getComparator(order, orderBy)
                        )
                            .slice(
                                page * rowsPerPage,
                                page * rowsPerPage + rowsPerPage
                            )
                            .map((row, index) => {
                                const rowId = row[props.rowIdField];
                                const isExpandedRow = props.tableProps.expandedIds.includes(
                                    rowId
                                );
                                return (
                                    <Row
                                        key={`row_${rowId}`}
                                        row={row}
                                        headers={props.tableHeaders}
                                        isExpanded={isExpandedRow}
                                        isExpandable={true}
                                        expandableComponent={
                                            props.expandableComponent
                                        }
                                        rowIdField={props.rowIdField}
                                        onExpandMethod={props.onExpandMethod}
                                        onUnexpandMethod={
                                            props.onUnexpandMethod
                                        }
                                        onRowClickMethod={
                                            props.onRowClickMethod
                                        }
                                        changeColorOnSelect={
                                            changeRowColorOnSelect
                                        }
                                        {...props.expandableProps}
                                    />
                                );
                            })}
                        {emptyRows > 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={props.tableHeaders.length}
                                />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {paginationComponent}
        </Box>
    );
};
